/**
 * Remember where a feed was scrolled to, so returning to it does not start at
 * the top.
 *
 * Needed because listings moved from a modal to a route. A modal left the home
 * page mounted underneath, so its scroll position survived for free. Now
 * `AppRoutes` renders with `key={location.pathname}`, which REMOUNTS the home
 * page on back — and `PageTemplate` scrolls to 0 on mount. Without this, every
 * traveller who opened a listing and pressed Back landed at the top of the
 * feed, having lost their place.
 *
 * sessionStorage, not localStorage: a position is only meaningful within the
 * tab's own history.
 */

const KEY_PREFIX = 'vkch:scroll:';

// The feed grows as listings arrive over onSnapshot, so at mount the page is
// usually too short to scroll to the saved offset. Retry across a few frames
// until the target is reachable rather than clamping to a wrong position.
const MAX_ATTEMPTS = 30;

export function saveScrollPosition(key, y) {
  try {
    sessionStorage.setItem(`${KEY_PREFIX}${key}`, String(Math.round(y)));
  } catch (e) {
    // Private browsing / quota. Losing a scroll position is not worth throwing.
  }
}

export function readScrollPosition(key) {
  try {
    const raw = sessionStorage.getItem(`${KEY_PREFIX}${key}`);
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch (e) {
    return null;
  }
}

export function clearScrollPosition(key) {
  try {
    sessionStorage.removeItem(`${KEY_PREFIX}${key}`);
  } catch (e) {
    // as above
  }
}

/**
 * Scroll to `target`, retrying while the document is still too short.
 * @returns {() => void} cancel, for effect cleanup
 */
export function restoreScrollPosition(target) {
  let frame = null;
  let attempts = 0;
  let cancelled = false;

  const attempt = () => {
    if (cancelled) return;
    attempts += 1;

    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    window.scrollTo(0, Math.min(target, Math.max(maxScroll, 0)));

    // Reached it, or ran out of patience. Stopping on `maxScroll >= target`
    // rather than on window.scrollY, because a browser may still be settling
    // the scroll when this runs.
    if (maxScroll >= target || attempts >= MAX_ATTEMPTS) return;
    frame = window.requestAnimationFrame(attempt);
  };

  frame = window.requestAnimationFrame(attempt);

  return () => {
    cancelled = true;
    if (frame !== null) window.cancelAnimationFrame(frame);
  };
}
